import { GoogleGenAI, Modality } from "@google/genai";
import { FactProcess } from "../types";

/**
 * Generate a draft legal declaration based on interview transcript.
 */
export const generateDraftDeclaration = async (intake: FactProcess): Promise<string> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_API_KEY });
  const transcriptText = intake.transcript.map(t => `${t.role}: ${t.text}`).join('\n');

  const prompt = `
    System Instruction: Act as a Senior Partner at Lluislaw APC. Tone is professional, sophisticated, and intellectually sharp.
    Task: Draft a formal first-person legal declaration for a ${intake.type} claim.
    Client: ${intake.client.name}. Strategic Focus: ${intake.client.focusDetails}
    Transcript Data:
    ${transcriptText}
    Format in Markdown. Introduction, Chronology, Hardship analysis, and Penalty of Perjury verification are required.
  `;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-pro-preview',
      contents: prompt,
      config: {
        temperature: 0.5,
        thinkingConfig: {
          thinkingBudget: 4000
        }
      }
    });
    return response.text || "Drafting error.";
  } catch (error) {
    console.error(error);
    return "Technical error occurred.";
  }
};

/**
 * Get the next question from AI based on interview context.
 */
export const getNextQuestion = async (intake: FactProcess): Promise<string | null> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_API_KEY });
  const transcriptText = intake.transcript.map(t => `${t.role}: ${t.text}`).join('\n');

  const systemPrompt = `
    You are Victoria, an AI Legal Intake Specialist for Lluislaw APC.
    You are conducting a ${intake.type} intake interview.
    Client: ${intake.client.name}
    Language: ${intake.client.language}
    Focus: ${intake.client.focusDetails || 'General declaration intake'}
    
    Current transcript:
    ${transcriptText}
    
        Task: Provide a professional, empathetic, and legally precise response.
    Ask the single most critical and strategically sound next question to progress the intake.
    Keep your response concise (under 40 words) for a smooth conversation.
    If transcript is empty, introduce yourself and ask the first opening question.
    Respond in ${intake.client.language}.
    Return ONLY your response text, nothing else.
  `;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.0-flash',
      contents: systemPrompt,
      config: { temperature: 0.7 }
    });
    return response.text || null;
  } catch (error) {
    console.error('[Gemini] Question generation error:', error);
    throw error;
  }
};

/**
 * Generate speech audio from text using Gemini.
 */
export const generateSpeech = async (text: string): Promise<string | null> => {
  const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_API_KEY });

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.0-flash',
      contents: [{ text: `Convert to natural speech: ${text}` }],
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } }
        }
      }
    });

    const audioData = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
    return audioData || null;
  } catch (error) {
    console.error('[Gemini] Speech generation error:', error);
    return null;
  }
};
